import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  addDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../../../firebaseConfig';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useSimplifiedSecurity } from '../../../hooks/useSimplifiedSecurity';

export const useStockMovements = () => {
  const { organizationId, isApproved } = useOrganization();
  const [stockMovements, setStockMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use simplified security for permissions
  const { canManageInventory, canViewInventory } = useSimplifiedSecurity();

  // Fetch stock movements with real-time updates
  useEffect(() => {
    if (!organizationId || !isApproved || !canViewInventory) {
      setStockMovements([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const movementsRef = collection(db, `organizations/${organizationId}/stock-movements`);
      const movementsQuery = query(movementsRef, orderBy('timestamp', 'desc'), limit(100));

      const unsubscribe = onSnapshot(
        movementsQuery,
        (snapshot) => {
          const movementsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate?.() || doc.data().timestamp,
            createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
          }));
          setStockMovements(movementsData);
          setLoading(false);
        },
        (error) => {
          setError(`Failed to fetch stock movements: ${error.message}`);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (error) {
      setError(`Failed to set up stock movements listener: ${error.message}`);
      setLoading(false);
    }
  }, [organizationId, isApproved, canViewInventory]);

  // Add a stock movement (stock in)
  const addStockIn = useCallback(async (movementData) => {
    if (!organizationId || !isApproved || !canManageInventory) {
      throw new Error('You do not have permission to add stock');
    }

    try {
      setError(null);
      const movementsRef = collection(db, `organizations/${organizationId}/stock-movements`);
      
      const newMovement = {
        ...movementData,
        type: 'stock_in',
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null,
        operation: 'add'
      };

      const docRef = await addDoc(movementsRef, newMovement);
      return { id: docRef.id, ...newMovement };
    } catch (error) {
      setError(`Failed to add stock in movement: ${error.message}`);
      throw error;
    }
  }, [organizationId, isApproved, canManageInventory]);

  // Add a stock movement (stock out)
  const addStockOut = useCallback(async (movementData) => {
    if (!organizationId || !isApproved || !canManageInventory) {
      throw new Error('You do not have permission to remove stock');
    }

    try {
      setError(null);
      const movementsRef = collection(db, `organizations/${organizationId}/stock-movements`);
      
      const newMovement = {
        ...movementData,
        type: 'stock_out',
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null,
        operation: 'remove'
      };

      const docRef = await addDoc(movementsRef, newMovement);
      return { id: docRef.id, ...newMovement };
    } catch (error) {
      setError(`Failed to add stock out movement: ${error.message}`);
      throw error;
    }
  }, [organizationId, isApproved, canManageInventory]);

  // Add a stock adjustment
  const addStockAdjustment = useCallback(async (movementData) => {
    if (!organizationId || !isApproved || !canManageInventory) {
      throw new Error('You do not have permission to adjust stock');
    }

    try {
      setError(null);
      const movementsRef = collection(db, `organizations/${organizationId}/stock-movements`);
      
      const newMovement = {
        ...movementData,
        type: 'adjustment',
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null,
        operation: 'adjust'
      };

      const docRef = await addDoc(movementsRef, newMovement);
      return { id: docRef.id, ...newMovement };
    } catch (error) {
      setError(`Failed to add stock adjustment: ${error.message}`);
      throw error;
    }
  }, [organizationId, isApproved, canManageInventory]);

  // Get movements by product ID
  const getMovementsByProduct = useCallback((productId) => {
    return stockMovements.filter(movement => movement.productId === productId);
  }, [stockMovements]);

  // Get movements by type
  const getMovementsByType = useCallback((type) => {
    return stockMovements.filter(movement => movement.type === type);
  }, [stockMovements]);

  // Get movements by date range
  const getMovementsByDateRange = useCallback((startDate, endDate) => {
    return stockMovements.filter(movement => {
      const movementDate = movement.timestamp;
      return movementDate >= startDate && movementDate <= endDate;
    });
  }, [stockMovements]);

  // Get recent movements (last N days)
  const getRecentMovements = useCallback((days = 7) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return stockMovements.filter(movement => {
      const movementDate = movement.timestamp;
      return movementDate >= cutoffDate;
    });
  }, [stockMovements]);

  // Get movements by reason
  const getMovementsByReason = useCallback((reason) => {
    return stockMovements.filter(movement => movement.reason === reason);
  }, [stockMovements]);

  // Get stock movement statistics
  const getMovementStats = useCallback(() => {
    const stats = {
      totalMovements: stockMovements.length,
      stockIn: 0,
      stockOut: 0,
      adjustments: 0,
      totalStockIn: 0,
      totalStockOut: 0,
      byReason: {},
      byProduct: {}
    };

    stockMovements.forEach(movement => {
      // Count by type
      if (movement.type === 'stock_in') {
        stats.stockIn++;
        stats.totalStockIn += movement.quantity || 0;
      } else if (movement.type === 'stock_out') {
        stats.stockOut++;
        stats.totalStockOut += movement.quantity || 0;
      } else if (movement.type === 'adjustment') {
        stats.adjustments++;
      }

      // Count by reason
      const reason = movement.reason || 'unknown';
      stats.byReason[reason] = (stats.byReason[reason] || 0) + 1;

      // Count by product
      const productId = movement.productId;
      if (productId) {
        if (!stats.byProduct[productId]) {
          stats.byProduct[productId] = {
            totalMovements: 0,
            stockIn: 0,
            stockOut: 0,
            totalStockIn: 0,
            totalStockOut: 0
          };
        }
        stats.byProduct[productId].totalMovements++;
        
        if (movement.type === 'stock_in') {
          stats.byProduct[productId].stockIn++;
          stats.byProduct[productId].totalStockIn += movement.quantity || 0;
        } else if (movement.type === 'stock_out') {
          stats.byProduct[productId].stockOut++;
          stats.byProduct[productId].totalStockOut += movement.quantity || 0;
        }
      }
    });

    return stats;
  }, [stockMovements]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Data
    stockMovements,
    loading,
    error,
    
    // Permissions
    canManageInventory,
    canViewInventory,
    
    // Actions
    addStockIn,
    addStockOut,
    addStockAdjustment,
    
    // Queries
    getMovementsByProduct,
    getMovementsByType,
    getMovementsByDateRange,
    getRecentMovements,
    getMovementsByReason,
    getMovementStats,
    
    // Utilities
    clearError
  };
}; 